import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FakeLink } from '@/core/FakeLink';
import { useForceUpdate } from '@/core/hooks/useForceUpdate.hook';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { canWriteItem, isAdmin } from '@/services/authorization.service';
import { fetchConfiguration, isConditionMonitorNotificationsEnabled } from '@/services/systemConfiguration.utilities';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { useVantageWorkbookActions } from '@/vantage/vantageWorkbook.store';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqSystemApi } from '@seeqdev/api/endpoint/SystemApi';
import { Alert } from '@seeqdev/qomponents';
import type { StoppedConditionMonitorState } from '@/vantage/types/VantageWorkbook.types';
import type { ScalarPropertyV1 } from '@seeqdev/api/model/models';

interface VantageConditionMonitorStoppedWarningProps {
  stoppedConditionMonitorState: StoppedConditionMonitorState;
}

/**
 * Warning message displayed when a Vantage condition monitor is disabled and/or archived.
 */
export const VantageConditionMonitorStoppedWarning = ({
  stoppedConditionMonitorState,
}: VantageConditionMonitorStoppedWarningProps) => {
  const { t } = useTranslation();

  const [show, setShow] = useState(true);
  const { setStoppedConditionMonitorState } = useVantageWorkbookActions();

  const [, forceUpdate] = useForceUpdate();
  const isGloballyEnabled = isConditionMonitorNotificationsEnabled();
  const hasWritePermission = canWriteItem(stoppedConditionMonitorState);
  const isUserAdmin = isAdmin();

  if (!show) {
    return null;
  }

  /**
   * If a condition monitor is marked as enabled but does not have a job ID, it indicates that the job was deleted.
   * In this case, we disable the condition monitor temporarily so that it can be re-enabled properly to kick off a new job.
   */
  const disableConditionMonitorIfJobDoesNotExist = async () => {
    if (stoppedConditionMonitorState.isEnabled && !stoppedConditionMonitorState.conditionMonitorJobId) {
      await sqItemsApi.setProperties([{ name: SeeqNames.Properties.Enabled, value: false }], {
        id: stoppedConditionMonitorState.id,
      });
    }
  };

  const enableIndividualConditionMonitor = async () => {
    const isConditionMonitorDisabled =
      !stoppedConditionMonitorState.isEnabled || !stoppedConditionMonitorState.conditionMonitorJobId;

    await disableConditionMonitorIfJobDoesNotExist();

    await sqItemsApi.setProperties(
      new Array<ScalarPropertyV1>()
        .concat(stoppedConditionMonitorState.isArchived ? [{ name: SeeqNames.Properties.Archived, value: false }] : [])
        .concat(isConditionMonitorDisabled ? [{ name: SeeqNames.Properties.Enabled, value: true }] : []),
      { id: stoppedConditionMonitorState.id },
    );
  };

  const handleEnableFromVantage = async () => {
    try {
      await enableIndividualConditionMonitor();
      successToast({ messageKey: 'VANTAGE.CM_WARNING.ENABLE_SUCCESS' });
      setStoppedConditionMonitorState(undefined);
    } catch (e) {
      errorToast({ httpResponseOrError: e });
    }
  };

  const handleEnableGlobally = async () => {
    try {
      await sqSystemApi.setConfigurationOptions({
        configurationOptions: [{ path: 'Features/Notifications/ConditionMonitors/Enabled', value: true }],
      });
      await fetchConfiguration();
      forceUpdate();
    } catch (e) {
      errorToast({ httpResponseOrError: e });
      return;
    }

    try {
      await enableIndividualConditionMonitor();
      successToast({ messageKey: 'VANTAGE.CM_WARNING.ENABLE_GLOBALLY_SUCCESS' });
      setStoppedConditionMonitorState(undefined);
    } catch (e) {
      errorToast({ httpResponseOrError: e });
    }
  };

  const renderAction = () => {
    if (isGloballyEnabled) {
      if (hasWritePermission) {
        return <FakeLink onClick={handleEnableFromVantage}>{t('ENABLE')}</FakeLink>;
      }
      return <>{t('VANTAGE.CM_WARNING.CONTACT_OWNER')}</>;
    }

    if (isUserAdmin) {
      return <FakeLink onClick={handleEnableGlobally}>{t('VANTAGE.CM_WARNING.ENABLE_ALL')}</FakeLink>;
    }
    return <>{t('VANTAGE.CM_WARNING.CONTACT_ADMIN')}</>;
  };

  return (
    <Alert
      id="vantageConditionMonitorStopped"
      variant="warning"
      dismissible={true}
      extraClassNames="warningBannerHeight"
      onClose={() => setShow(false)}>
      <span data-testid="vantageConditionMonitorStoppedWarning">
        {t(isGloballyEnabled ? 'VANTAGE.CM_WARNING.DISABLED_FOR_ROOM' : 'VANTAGE.CM_WARNING.DISABLED_GLOBALLY')}{' '}
        {renderAction()}
      </span>
    </Alert>
  );
};
